import { FC, lazy, LazyExoticComponent, Suspense, useEffect, useState } from 'react';
import type { GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Error from 'next/error';
import { Box } from '@mui/material';
import { Experiments } from '@/libs/experiments';
import { PricingSkeleton } from '@/features/pricingV2';
import { MetaData } from '@/components/MetaData';
import { IProductEntity, PricingRepository } from '@/features/common/pricing';

type PricingPageState =
  | {
      state: 'loading';
    }
  | {
      state: 'error';
    }
  | {
      state: 'success';
      products: IProductEntity[];
      isTrialEnabled: boolean;
      Component: LazyExoticComponent<
        FC<{
          products: IProductEntity[];
          isTrialEnabled: boolean;
        }>
      >;
    };

const PricingPageOld = lazy(() =>
  import('@/features/pricing/ui/Pricing').then(({ Pricing }) => ({
    default: Pricing,
  })),
);

const PricingPageNew = lazy(() =>
  import('@/features/pricingV2/ui/PricingPage').then(({ PricingPage }) => ({
    default: PricingPage,
  })),
);

let browserPageStateCache: PricingPageState = { state: 'loading' };

const usePricingPageState = (): PricingPageState => {
  const [state, setState] = useState<PricingPageState>(() => {
    if (typeof window === 'undefined') {
      return { state: 'loading' };
    }

    return browserPageStateCache;
  });

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        const pricingRepository = new PricingRepository();
        const experiments = new Experiments();

        const query = new URLSearchParams(window.location.search);
        const preview_plan_id = query.get('preview_plan_id');
        const preview_region_id = query.get('preview_region_id');

        const options: {
          ip?: string;
          searchParams?: Record<string, string>;
        } = {};

        if (
          typeof preview_plan_id === 'string' &&
          typeof preview_region_id === 'string'
        ) {
          options.searchParams = {
            preview_plan_id,
            preview_region_id,
          };
        }

        const [products, isTrialEnabled, variant] = await Promise.all([
          pricingRepository.getProducts(options),
          pricingRepository.isTrialEnabled().catch(() => false),
          experiments.getVariant('pricing-pages', 'new'),
        ]);

        browserPageStateCache = {
          state: 'success',
          products: products || [],
          isTrialEnabled: isTrialEnabled ?? false,
          Component: variant === 'new' ? PricingPageNew : PricingPageOld,
        };

        setState(browserPageStateCache);
      } catch {
        setState({ state: 'error' });
      }
    })();
  }, []);

  return state;
};

const PricingPage: NextPage<{}> = () => {
  const state = usePricingPageState();

  if (state.state === 'loading') {
    return (
      <Box>
        <MetaData />
        <PricingSkeleton />
      </Box>
    );
  }

  if (state.state === 'error') {
    return (
      <Box>
        <MetaData />
        <Error statusCode={500} />
      </Box>
    );
  }

  return (
    <Box>
      <MetaData />
      <Suspense fallback={<PricingSkeleton />}>
        <state.Component
          products={state.products}
          isTrialEnabled={state.isTrialEnabled}
        />
      </Suspense>
    </Box>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const translations = await serverSideTranslations('en', [
    'common',
    'metaData',
    'pricing',
    'pricingV2',
  ]);

  return {
    props: {
      ...translations,
    },
  };
};

export default PricingPage;
